import { Capacitor } from '@capacitor/core';

// ─── Shared interface ────────────────────────────────────────────────────────

interface ILocalDbService {
  addPendingOrder(order: any): Promise<void>;
  getPendingOrders(): Promise<any[]>;
  removePendingOrder(id: string): Promise<void>;
  getPendingOrderById(id: string): Promise<any | null>;
  addKOTJob(job: { id: string; orderId: string; orderNumber: string; restaurantId: string; printerIds?: string[]; queuedAt: string }): Promise<void>;
  getPendingKOTJobs(): Promise<any[]>;
  removeKOTJob(id: string): Promise<void>;
  addOrderUpdate(update: { id: string; orderId: string; status: string; updatedAt: string }): Promise<void>;
  getPendingOrderUpdates(): Promise<any[]>;
  removeOrderUpdate(id: string): Promise<void>;
  setCache(key: string, value: any): Promise<void>;
  getCache(key: string): Promise<any | null>;
  getStoreSummary(): Promise<{ orders: number; kots: number; statusUpdates: number; cacheKeys: number }>;
}

// ─── SQLite implementation (Web + Android + iOS) ─────────────────────────────
// On native  → real .db file on device storage
// On web     → jeep-sqlite writes to browser Origin Private File System (OPFS)
//              visible in Chrome DevTools → Application → Storage → OPFS
//
// Uses dynamic import so the plugin is never statically bundled;
// it loads lazily on first database operation.

class SQLiteDbService implements ILocalDbService {
  private sqliteConn: any = null;
  private db: any = null;
  private readonly DB_NAME = 'MenuMasterOfflineDB';
  private initPromise: Promise<void> | null = null;

  private async init(): Promise<void> {
    if (this.db) return;
    if (this.initPromise) return this.initPromise;
    this.initPromise = this._doInit();
    return this.initPromise;
  }

  private async _doInit(): Promise<void> {
    // On web, jeep-sqlite custom element is not registered so SQLite is unavailable.
    // AdaptiveDbService catches the thrown error and falls back to IndexedDB.
    if (!Capacitor.isNativePlatform()) {
      const el = document.querySelector('jeep-sqlite');
      if (!el) throw new Error('jeep-sqlite element not present — SQLite unavailable on web');
    }

    const { CapacitorSQLite, SQLiteConnection } = await import('@capacitor-community/sqlite');
    this.sqliteConn = new SQLiteConnection(CapacitorSQLite);

    if (!Capacitor.isNativePlatform()) {
      await this.sqliteConn.initWebStore();
    }

    const consistency = await this.sqliteConn.checkConnectionsConsistency();
    const isConn = (await this.sqliteConn.isConnection(this.DB_NAME, false)).result;
    if (consistency.result && isConn) {
      this.db = await this.sqliteConn.retrieveConnection(this.DB_NAME, false);
    } else {
      this.db = await this.sqliteConn.createConnection(this.DB_NAME, false, 'no-encryption', 1, false);
    }
    await this.db.open();
    await this.db.execute(`
      CREATE TABLE IF NOT EXISTS orders_queue (
        id TEXT PRIMARY KEY, data TEXT NOT NULL, updated_at TEXT
      );
      CREATE TABLE IF NOT EXISTS kot_queue (
        id TEXT PRIMARY KEY, data TEXT NOT NULL, queued_at TEXT
      );
      CREATE TABLE IF NOT EXISTS order_updates_queue (
        id TEXT PRIMARY KEY, data TEXT NOT NULL, updated_at TEXT
      );
      CREATE TABLE IF NOT EXISTS meta_cache (
        key TEXT PRIMARY KEY, value TEXT NOT NULL, timestamp INTEGER
      );
    `);
  }

  async addPendingOrder(order: any): Promise<void> {
    await this.init();
    await this.db.run('INSERT OR REPLACE INTO orders_queue (id, data, updated_at) VALUES (?, ?, ?)',
      [order.id, JSON.stringify(order), new Date().toISOString()]);
  }

  async getPendingOrders(): Promise<any[]> {
    await this.init();
    const res = await this.db.query('SELECT data FROM orders_queue');
    return (res.values || []).map((r: any) => JSON.parse(r.data));
  }

  async removePendingOrder(id: string): Promise<void> {
    await this.init();
    await this.db.run('DELETE FROM orders_queue WHERE id = ?', [id]);
  }

  async getPendingOrderById(id: string): Promise<any | null> {
    await this.init();
    const res = await this.db.query('SELECT data FROM orders_queue WHERE id = ?', [id]);
    const rows = res.values || [];
    return rows.length > 0 ? JSON.parse(rows[0].data) : null;
  }

  async addKOTJob(job: any): Promise<void> {
    await this.init();
    await this.db.run('INSERT OR REPLACE INTO kot_queue (id, data, queued_at) VALUES (?, ?, ?)',
      [job.id, JSON.stringify(job), job.queuedAt]);
  }

  async getPendingKOTJobs(): Promise<any[]> {
    await this.init();
    const res = await this.db.query('SELECT data FROM kot_queue ORDER BY queued_at');
    return (res.values || []).map((r: any) => JSON.parse(r.data));
  }

  async removeKOTJob(id: string): Promise<void> {
    await this.init();
    await this.db.run('DELETE FROM kot_queue WHERE id = ?', [id]);
  }

  async addOrderUpdate(update: any): Promise<void> {
    await this.init();
    await this.db.run('INSERT OR REPLACE INTO order_updates_queue (id, data, updated_at) VALUES (?, ?, ?)',
      [update.id, JSON.stringify(update), update.updatedAt]);
  }

  async getPendingOrderUpdates(): Promise<any[]> {
    await this.init();
    const res = await this.db.query('SELECT data FROM order_updates_queue ORDER BY updated_at');
    return (res.values || []).map((r: any) => JSON.parse(r.data));
  }

  async removeOrderUpdate(id: string): Promise<void> {
    await this.init();
    await this.db.run('DELETE FROM order_updates_queue WHERE id = ?', [id]);
  }

  async setCache(key: string, value: any): Promise<void> {
    await this.init();
    await this.db.run('INSERT OR REPLACE INTO meta_cache (key, value, timestamp) VALUES (?, ?, ?)',
      [key, JSON.stringify(value), Date.now()]);
  }

  async getCache(key: string): Promise<any | null> {
    await this.init();
    const res = await this.db.query('SELECT value FROM meta_cache WHERE key = ?', [key]);
    const rows = res.values || [];
    return rows.length > 0 ? JSON.parse(rows[0].value) : null;
  }

  async getStoreSummary() {
    const [o, k, u] = await Promise.all([this.getPendingOrders(), this.getPendingKOTJobs(), this.getPendingOrderUpdates()]);
    return { orders: o.length, kots: k.length, statusUpdates: u.length, cacheKeys: 0 };
  }
}

// ─── IndexedDB fallback (used only if SQLite init fails) ─────────────────────

class IndexedDbService implements ILocalDbService {
  private db: IDBDatabase | null = null;
  private initPromise: Promise<void> | null = null;

  public init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = new Promise((resolve, reject) => {
      if (typeof window === 'undefined' || !window.indexedDB) { resolve(); return; }
      const req = indexedDB.open('MenuMasterOfflineDB_idb', 2);
      req.onupgradeneeded = (e: any) => {
        const db: IDBDatabase = e.target.result;
        ['orders_queue', 'kot_queue', 'order_updates_queue'].forEach(n => {
          if (!db.objectStoreNames.contains(n)) db.createObjectStore(n, { keyPath: 'id' });
        });
        if (!db.objectStoreNames.contains('meta_cache')) db.createObjectStore('meta_cache', { keyPath: 'key' });
      };
      req.onsuccess = (e: any) => { this.db = e.target.result; resolve(); };
      req.onerror   = (e: any) => reject(e.target.error);
    });
    return this.initPromise;
  }

  private async store(name: string, mode: IDBTransactionMode): Promise<IDBObjectStore> {
    await this.init();
    if (!this.db) throw new Error('IndexedDB not available');
    return this.db.transaction(name, mode).objectStore(name);
  }

  private op<T>(r: IDBRequest<T>): Promise<T> {
    return new Promise((res, rej) => { r.onsuccess = () => res(r.result); r.onerror = (e: any) => rej(e.target.error); });
  }

  async addPendingOrder(o: any)        { await this.op((await this.store('orders_queue',        'readwrite')).put(o)); }
  async getPendingOrders()             { try { return await this.op((await this.store('orders_queue',        'readonly')).getAll()) as any[]; } catch { return []; } }
  async removePendingOrder(id: string) { await this.op((await this.store('orders_queue',        'readwrite')).delete(id)); }
  async getPendingOrderById(id: string){ try { return (await this.op((await this.store('orders_queue', 'readonly')).get(id))) ?? null; } catch { return null; } }
  async addKOTJob(j: any)              { await this.op((await this.store('kot_queue',           'readwrite')).put(j)); }
  async getPendingKOTJobs()            { try { return await this.op((await this.store('kot_queue',           'readonly')).getAll()) as any[]; } catch { return []; } }
  async removeKOTJob(id: string)       { await this.op((await this.store('kot_queue',           'readwrite')).delete(id)); }
  async addOrderUpdate(u: any)         { await this.op((await this.store('order_updates_queue', 'readwrite')).put(u)); }
  async getPendingOrderUpdates()       { try { return await this.op((await this.store('order_updates_queue', 'readonly')).getAll()) as any[]; } catch { return []; } }
  async removeOrderUpdate(id: string)  { await this.op((await this.store('order_updates_queue', 'readwrite')).delete(id)); }
  async setCache(k: string, v: any)    { await this.op((await this.store('meta_cache',          'readwrite')).put({ key: k, value: v, timestamp: Date.now() })); }
  async getCache(k: string)            { try { const r: any = await this.op((await this.store('meta_cache', 'readonly')).get(k)); return r?.value ?? null; } catch { return null; } }
  async getStoreSummary()              { const [o,k,u] = await Promise.all([this.getPendingOrders(),this.getPendingKOTJobs(),this.getPendingOrderUpdates()]); return { orders: o.length, kots: k.length, statusUpdates: u.length, cacheKeys: 0 }; }
}

// ─── Adaptive factory ─────────────────────────────────────────────────────────
// Try SQLite on all platforms (web via jeep-sqlite OPFS, native via .db file).
// If SQLite init fails (old browser, no OPFS support), fall back to IndexedDB.

class AdaptiveDbService implements ILocalDbService {
  private impl: ILocalDbService | null = null;
  private resolvePromise: Promise<ILocalDbService> | null = null;

  private async resolve(): Promise<ILocalDbService> {
    if (this.impl) return this.impl;
    if (this.resolvePromise) return this.resolvePromise;
    this.resolvePromise = (async () => {
      try {
        const svc = new SQLiteDbService();
        await svc.getStoreSummary(); // triggers full init — throws if SQLite unavailable
        this.impl = svc;
        console.info('[LocalDb] Using SQLite storage');
      } catch (e) {
        console.warn('[LocalDb] SQLite unavailable, falling back to IndexedDB:', e);
        this.impl = new IndexedDbService();
      }
      return this.impl!;
    })();
    return this.resolvePromise;
  }

  async addPendingOrder(o: any)        { return (await this.resolve()).addPendingOrder(o); }
  async getPendingOrders()             { return (await this.resolve()).getPendingOrders(); }
  async removePendingOrder(id: string) { return (await this.resolve()).removePendingOrder(id); }
  async getPendingOrderById(id: string){ return (await this.resolve()).getPendingOrderById(id); }
  async addKOTJob(j: any)              { return (await this.resolve()).addKOTJob(j); }
  async getPendingKOTJobs()            { return (await this.resolve()).getPendingKOTJobs(); }
  async removeKOTJob(id: string)       { return (await this.resolve()).removeKOTJob(id); }
  async addOrderUpdate(u: any)         { return (await this.resolve()).addOrderUpdate(u); }
  async getPendingOrderUpdates()       { return (await this.resolve()).getPendingOrderUpdates(); }
  async removeOrderUpdate(id: string)  { return (await this.resolve()).removeOrderUpdate(id); }
  async setCache(k: string, v: any)    { return (await this.resolve()).setCache(k, v); }
  async getCache(k: string)            { return (await this.resolve()).getCache(k); }
  async getStoreSummary()              { return (await this.resolve()).getStoreSummary(); }
}

export const localDb: ILocalDbService = new AdaptiveDbService();
export default localDb;
